"use client"

import { useEffect, useState } from "react"

/**
 * Is a fixed element currently floating over a section that asked to be left
 * alone?
 *
 * BOOTH's chrome is two things pinned to the viewport — the wordmark at the top
 * and the launcher at the bottom — and both sit ON the page rather than in a
 * bar. Over ordinary white sections that reads fine. Over the floor plan's
 * black field it doesn't: there's nothing for them to sit against, so they
 * neither disappear nor resolve, and the wordmark in particular ghosts straight
 * through the section's own header. Sections opt out by carrying
 * `data-floating-nav="hide"`.
 *
 * The return says WHICH kind of zone is under the band, because the two want
 * different answers: an "overlay" (a fixed, full-screen surface the user
 * opened) can take the chrome away, while a "section" in the page flow is only
 * scrolled past, so a control sitting over it must stay reachable and merely
 * restyle itself.
 *
 * Overlap is measured against the caller's OWN band rather than with a
 * whole-element IntersectionObserver, because these sections are taller than
 * the screen: "is any of it visible" stays true for most of the scroll, which
 * would hide the chrome far too early and for far too long. A band asks the
 * only question that matters — is the section under this specific element right
 * now.
 *
 * Offsets are given from the edge the element is pinned to, and the band is
 * resolved inside the measurement — passing absolute pixels would have baked in
 * whatever window.innerHeight was at first render, so a bottom-anchored element
 * would test the wrong strip of the page after any resize.
 *
 * @param anchor   which viewport edge the element is pinned to
 * @param near     distance from that edge to the element's near side, px
 * @param far      distance from that edge to its far side, px
 */
export type ObscuringZone = "none" | "overlay" | "section"

export function useObscuredByZone(
  anchor: "top" | "bottom",
  near: number,
  far: number,
): ObscuringZone {
  const [obscured, setObscured] = useState<ObscuringZone>("none")

  useEffect(() => {
    let frame = 0

    const measure = () => {
      frame = 0
      const zones = document.querySelectorAll<HTMLElement>('[data-floating-nav="hide"]')
      if (zones.length === 0) {
        setObscured("none")
        return
      }
      const bandTop = anchor === "top" ? near : window.innerHeight - far
      const bandBottom = anchor === "top" ? far : window.innerHeight - near
      let hit: ObscuringZone = "none"
      zones.forEach((zone) => {
        const r = zone.getBoundingClientRect()
        if (r.top >= bandBottom || r.bottom <= bandTop) return
        // A fixed zone is a full-screen overlay the user opened (the map, a
        // thread panel, the notifications sheet): it replaces the page, so the
        // chrome genuinely belongs out of the way and the user can always get
        // back with the overlay's own close control. A zone in the page flow is
        // just a section the user scrolled past, and fading the launcher there
        // leaves no way to reach the menu at all — those only restyle it.
        const fixed = getComputedStyle(zone).position === "fixed"
        if (fixed) hit = "overlay"
        else if (hit === "none") hit = "section"
      })
      setObscured(hit)
    }

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }

    measure()
    // capture:true so nested scrollers (overflow-y:auto containers) count too —
    // scroll doesn't bubble.
    document.addEventListener("scroll", onScroll, { capture: true, passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true } as EventListenerOptions)
      window.removeEventListener("resize", onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [anchor, near, far])

  return obscured
}
